import { KeyValuePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, viewChild } from '@angular/core';
import { IScopingItemGroupMapping } from '@app/cem/project/scoping-items/models/iscoping-item-group-mapping';
import { ItemManagementService } from "@app/cem/project/services/item-management.service";
import { LookupService } from "@app/cem/project/services/lookup.service";
import { ErrorService } from '@app/common/services/error.service';
import { PlexEnvironment } from '@app/env';
import { PxdAgGridCheckboxRenderer } from '@app/shared/aggrid/pxd-ag-grid-cell-renderers';
import { PxdAgGridMultiSelectEditorComponent } from '@app/shared/aggrid/pxd-ag-grid-multi-select-editor';
import { PxdAgGridNumericEditorComponent } from '@app/shared/aggrid/pxd-ag-grid-numeric-editor';
import { PxdAgEditGridDefaultColumnDef, PxdAgGridColumnTypes, PxdAgGridOptions, PxdAgGridSideBarOptions, PxdAgGridStatusBarOptions } from '@app/shared/aggrid/pxd-ag-grid-options';
import { PxdAgGridSelectEditorComponent } from '@app/shared/aggrid/pxd-ag-grid-select-editor';
import { PxdAgGridActionLinkComponent } from '@app/shared/kendoui/grid/components/pxd-ag-grid-action-link.component';
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { ICapacitySubCategoryDto } from '@app/shared/models/dtos/capacity-sub-category';
import { ICostCodeCostTypeDto, ICostTypeDto, IFunctionDto, IItemDto, IProjectTypeDto, IUnitOfMeasureDto } from "@app/shared/models/dtos/dtos";
import { IItemCategoryDto } from '@app/shared/models/dtos/item-category';
import { IItemSubCategoryDto } from '@app/shared/models/dtos/item-sub-category';
import { ItemScopingCategory } from '@app/shared/models/item-scoping-category';
import { ItemDtoValidator } from "@app/shared/models/validators/item-validator";
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { WindowComponent } from '@progress/kendo-angular-dialog';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, ColGroupDef, GridApi } from 'ag-grid-community';
import moment from 'moment';
import { Observable, Subject } from 'rxjs';
import { ItemsDetailsRatesComponent } from '../items-details-rates/items-details-rates.component';

@Component({
  selector: 'app-items-index',
  templateUrl: './items-index.component.html',
  styleUrls: ['./items-index.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    KeyValuePipe,
    ButtonDirective,
    AgGridAngular,
    WindowComponent,
    ItemsDetailsRatesComponent,
  ],
})

export class ItemsIndexComponent implements OnInit {
  private itemManagementService = inject(ItemManagementService);
  private lookupService = inject(LookupService);
  private errorService = inject(ErrorService);
  public environment = inject(PlexEnvironment);

  public icons = kendoIcons;

  public IItemDtos: Array<IItemDto> = new Array<IItemDto>();
  public IProjectTypeDtos: Array<IProjectTypeDto> = new Array<IProjectTypeDto>();
  public IFunctionDtos: Array<IFunctionDto> = new Array<IFunctionDto>();
  public ICostCodeCostTypeDtos: Array<ICostCodeCostTypeDto> = new Array<ICostCodeCostTypeDto>();
  public ICostTypeDtos: Array<ICostTypeDto> = new Array<ICostTypeDto>();
  public IItemTypeDtos: Array<IItemCategoryDto> = new Array<IItemCategoryDto>();
  public IItemSubTypeDtos: Array<IItemSubCategoryDto> = new Array<IItemSubCategoryDto>();
  public IUnitOfMeasureDtos: Array<IUnitOfMeasureDto> = new Array<IUnitOfMeasureDto>();

  public ICapacitySubCategoryDtos: Array<ICapacitySubCategoryDto> = new Array<ICapacitySubCategoryDto>();
  public ICapacitySubCategoryDtoMap: Map<number, ICapacitySubCategoryDto>;

  public ValidationErrors: Map<string, Array<string>> = new Map<string, Array<string>>();
  public ItemId: number;
  public Title: string;
  public showItemDetails: boolean = false;
  public editedEntities: Map<number, IItemDto> = new Map<any, IItemDto>();
  private itemDtoValidator = new ItemDtoValidator();
  public refreshRateSubject: Subject<void> = new Subject<void>();
  private initLoaded: Subject<boolean> = new Subject();
  private ScopingItemGroupMapping: IScopingItemGroupMapping[];

  public gridOptions: PxdAgGridOptions = {
    enableCharts: true,
    cellSelection: true,
    rowGroupPanelShow: 'always',
    defaultColDef: PxdAgEditGridDefaultColumnDef,
    statusBar: PxdAgGridStatusBarOptions,
    sideBar: PxdAgGridSideBarOptions,
    columnTypes: PxdAgGridColumnTypes,
    processCellForClipboard(params) {
      if (params.column.getColId() === 'CapacitySubCategoryIds') {
        let colDef = params.column.getColDef();
        if (colDef.valueFormatter && typeof (colDef.valueFormatter) == 'function') {
          //use the custom cell render to render to excel also
          const valueFormatterParams = {
            ...params,
            data: params.node.data,
            node: params.node!,
            colDef: params.column.getColDef()
          };
          return colDef.valueFormatter(valueFormatterParams);
        }
      }
      return params.value;
    },
    columnDefs: [
      {
        field: 'ItemId',
        headerName: 'Item ID',
        editable: false,
        enableRowGroup: false,
        hide: true,
        width: 100
      },
      {
        field: 'ItemNumber',
        editable: false,
        headerName: 'Item Number',
        enableRowGroup: true,
        type: ['numberColumn'],
        cellRenderer: PxdAgGridActionLinkComponent,
        cellEditorSelector: params => {
          return { component: PxdAgGridNumericEditorComponent };
        },
        cellRendererParams: params => {
          return {
            id: 'ItemId',
            callAction: (data) => {
              if (data <= 0) {
                this.ValidationErrors.set('Cannot edit Item Rates on new Item until new Item is initially saved', ["Item Number: " + params.value.toString()]);
                return;
              }
              this.ItemId = data;
              this.Title = `${params.data.ItemNumber} - ${params.data.Name}`;
              this.showItemDetails = true;
              this.refreshRateSubject.next();
            }
          };
        },
        width: 125
      },
      {
        field: 'Name',
        headerName: 'Name',
        enableRowGroup: true,
        editable: true
      },
      {
        field: 'FunctionId',
        headerName: 'Function',
        editable: true,
        enableRowGroup: true,
        valueGetter: params => {
          return this.getIFunctionDtosById(params.data.FunctionId);
        },
        filterParams: {
          valueGetter: params => {
            return this.getIFunctionDtosById(params.data.FunctionId);
          }
        },
        cellEditorSelector: params => {
          return {
            component: PxdAgGridSelectEditorComponent,
            params: {
              values: Array.from(this.IFunctionDtos).filter(s => s.FunctionId !== 0).map(d => {
                return { value: d.FunctionId, text: d.Description };
              }),
              value: params.data.FunctionId
            }
          };
        }
      },
      {
        field: 'ItemCategoryId',
        headerName: 'Item Category',
        editable: true,
        enableRowGroup: true,
        valueGetter: params => {
          return this.getIItemTypeDtosById(params.data.ItemCategoryId);
        },
        filterParams: {
          valueGetter: params => {
            return this.getIItemTypeDtosById(params.data.ItemCategoryId);
          }
        },
        cellEditorSelector: params => {
          return {
            component: PxdAgGridSelectEditorComponent,
            params: {

              values: Array.from(this.IItemTypeDtos).map(d => {
                return { value: d.ItemCategoryId, text: d.ItemCategoryName };
              }),
              value: params.data.ItemCategoryId,
            }
          };
        }
      },
      {
        field: 'ItemSubCategoryId',
        headerName: 'Item Sub Category',
        editable: true,
        enableRowGroup: true,
        valueGetter: params => {
          return this.getIItemSubTypeDtosById(params.data.ItemSubCategoryId);
        },
        filterParams: {
          valueGetter: params => {
            return this.getIItemSubTypeDtosById(params.data.ItemSubCategoryId);
          }
        },
        cellEditorSelector: params => {
          return {
            component: PxdAgGridSelectEditorComponent,
            params: {
              value: params.data.ItemSubCategoryId,
              values: this.IItemSubTypeDtos.filter(d => d.ItemCategoryId === params.data.ItemCategoryId).map(d => {
                return { value: d.ItemSubCategoryId, text: d.ItemSubCategoryName };
              }),
            }
          };
        }
      },
      {
        field: 'ItemScopingCategoryId',
        headerName: 'Item Scoping Category',
        editable: true,
        cellEditorSelector: params => {
          return {
            component: PxdAgGridSelectEditorComponent,
            params: {
              value: params.data.ItemScopingCategoryId,
              values: this.ItemScopingCategoryList.map(d => {
                return {
                  value: d.ItemScopingCategoryId,
                  text: d.Description
                }
              })
            }
          }
        },
        filterParams: { valueGetter: params => { return this.ItemScopingCategoryMap[params.data.ItemScopingCategoryId] } },
        valueSetter: params => {
          params.data.ItemScopingCategoryId = params.newValue;
          return true;
        },
        valueGetter: params => { return this.ItemScopingCategoryMap[params.data.ItemScopingCategoryId] }
      },
      {
        field: 'CapacitySubCategoryIds',
        headerName: 'Capacity Sub Categories',
        editable: true,
        width: 350,
        colId: 'CapacitySubCategoryIds',
        cellEditorSelector: params => {
          return {
            component: PxdAgGridMultiSelectEditorComponent,
            params: {
              value: params.data.CapacitySubCategoryIds,
              values: this.ICapacitySubCategoryDtos.map(d => {
                return {
                  value: d.CapacitySubCategoryId,
                  text: d.Description
                }
              })
            }
          }
        },
        valueSetter: params => {
          params.data.CapacitySubCategoryIds = params.newValue;
          return true;
        },
        valueGetter: params => {
          return this.getCapacitySubCategoriesFromIDArray(params);
        },
      },
      {
        field: 'CostType',
        headerName: 'Project Cost Type',
        editable: true,
        enableRowGroup: true,
        valueGetter: params => {
          return this.getICostTypeDtosById(params.data.CostType);
        },
        filterParams: {
          valueGetter: params => {
            return this.getICostTypeDtosById(params.data.CostType);
          }
        },
        cellEditorSelector: params => {
          const selectedValue = params.data?.CostCodeCostTypeId;
          const costCodeValue = this.ICostCodeCostTypeDtos.filter(t => t.CostCodeCostTypeId === selectedValue);
          const typeExists = costCodeValue.length > 0 ? this.ICostTypeDtos.some(t => t.CostTypeValue === costCodeValue[0].CostTypeDto?.CostTypeValue) : false;

          const src = typeExists
            ? this.ICostTypeDtos.filter(d => d.CostTypeId === costCodeValue[0].CostTypeId)
            : this.ICostTypeDtos;

          return {
            component: PxdAgGridSelectEditorComponent,
            params: {

              values: Array.from(src).map(d => {
                return {
                  value: d.CostTypeValue,
                  text: (d.CostTypeDescription)
                };
              }),
              value: params.data.CostTypeId,
            }
          };
        },
        width: 350
      },
      {
        field: 'CostCodeCostTypeId',
        headerName: 'Cost Code Cost Type',
        editable: true,
        enableRowGroup: true,
        valueGetter: params => {
          const selectedValue = params.data?.CostType;
          const typeExists = this.ICostTypeDtos.some(t => t.CostTypeValue === selectedValue);

          return this.getICostCodeCostTypeDtosById(params.data.CostCodeCostTypeId);
        },
        filterParams: {
          valueGetter: params => {
            return this.getICostCodeCostTypeDtosById(params.data.CostCodeCostTypeId);
          }
        },
        cellEditorSelector: params => {
          const selectedValue = params.data?.CostType;
          const typeExists = this.ICostTypeDtos.some(t => t.CostTypeValue === selectedValue);

          const data = this.ICostCodeCostTypeDtos.filter(d => d.CostTypeDto?.CostTypeCategoryId == 5);

          const src = typeExists
            ? data.filter(d => d.CostTypeDto?.CostTypeCategoryId == 5 && d.CostTypeDto?.CostTypeValue === selectedValue)
            : data;

          const values = src
            .map(d => ({
              value: d.CostCodeCostTypeId,
              text: `${d.CostCodeDto.CostCodeValue} - ${d.CostCodeDto.CostCodeDescription}`
            }))
            .sort((a, b) => a.text.localeCompare(b.text));

          return {
            component: PxdAgGridSelectEditorComponent,
            params: {
              values: values,
              value: params.data.CostCodeCostTypeId,
            }
          };
        },
        width: 350
      },
      {
        field: 'PrimaryUOM',
        headerName: 'Primary UOM',
        editable: true,
        enableRowGroup: true,
        valueGetter: params => {
          return this.getIUnitOfMeasureDtosById(params.data.PrimaryUOM)
        },
        width: 150,
        cellEditorSelector: params => {
          return {
            component: PxdAgGridSelectEditorComponent,
            params: {
              values: Array.from(this.IUnitOfMeasureDtos).map(d => {
                return {
                  value: d.UnitOfMeasureId,
                  text: d.Description
                };
              }),
              value: params.data.PrimaryUOM,
            }
          };
        }
      },
      {
        field: 'PartNumber',
        headerName: 'Part Number',
        editable: true
      },
      {
        field: 'OilCapacity',
        editable: true,
        headerName: 'Oil (BOPD)',
        type: ['numericColumn'],
        cellEditorSelector: params => {
          return { component: PxdAgGridNumericEditorComponent };
        },
        headerClass: 'center-aligned',
        cellStyle: { 'text-align': 'center' }
      },
      {
        field: 'GasCapacity',
        editable: true,
        headerName: 'Gas (MCFD)',
        type: ['numericColumn'],
        cellEditorSelector: params => {
          return { component: PxdAgGridNumericEditorComponent };
        },
        headerClass: 'center-aligned',
        cellStyle: { 'text-align': 'center' }
      },
      {
        field: 'WaterCapacity',
        editable: true,
        headerName: 'Water (BWPD)',
        type: 'numericColumn',
        cellEditorSelector: params => {
          return { component: PxdAgGridNumericEditorComponent };
        },
        headerClass: 'center-aligned',
        cellStyle: { 'text-align': 'center' }
      },
      {
        field: 'ElectricityCapacity',
        editable: true,
        headerName: 'Electricity Capacity',
        type: ['numericColumn'],
        cellEditorSelector: params => {
          return { component: PxdAgGridNumericEditorComponent };
        },
        headerClass: 'center-aligned',
        cellStyle: { 'text-align': 'center' },
      },
      {
        field: 'ElectricityDemand',
        editable: true,
        headerName: 'Electricity Demand',
        type: ['numericColumn'],
        cellEditorSelector: params => {
          return { component: PxdAgGridNumericEditorComponent };
        },
        headerClass: 'center-aligned',
        cellStyle: { 'text-align': 'center' }
      },
      {
        field: 'IsActive',
        editable: true,
        headerName: 'Is Active',
        cellRenderer: PxdAgGridCheckboxRenderer,
        width: 110
      },
      {
        field: 'UsePhaseStepStartDate',
        editable: true,
        headerName: 'Use Phase Step Start Date',
        cellRenderer: PxdAgGridCheckboxRenderer,
        width: 170
      }

    ],
    defaultExcelExportParams: {
      processCellCallback: params => {
        if (params.column.getColId() === 'CapacitySubCategoryIds') {
          let colDef = params.column.getColDef();
          let data = params.value;
          if (colDef.valueFormatter && typeof (colDef.valueFormatter) == 'function') {
            const valueFormatterParams = {
              ...params,
              data: params.node.data,
              node: params.node!,
              colDef: params.column.getColDef()
            };
            return colDef.valueFormatter(valueFormatterParams);
          }
        }
        return params.value;
      }
    }
  };

  private applyAfeColumnState(): void {
    if (!this.gridApi) return;

    const currentDefs = (this.gridApi.getColumnDefs() ?? []) as (ColDef | ColGroupDef)[];

    const newDefs: (ColDef | ColGroupDef)[] = currentDefs.map(def => {
      if ('children' in def) {
        return def;
      }
      const updated: ColDef = { ...def };
      const isCostType =
        (updated.colId === 'CostType') || (updated.field === 'CostType');

      const isCostCodeCostTypeId =
        (updated.colId === 'CostCodeCostTypeId') || (updated.field === 'CostCodeCostTypeId');

      if (isCostType) {
        updated.hide = false;
        updated.suppressColumnsToolPanel = false;
        updated.suppressFiltersToolPanel = false;
      }

      if (isCostCodeCostTypeId) {
        updated.headerName = 'GFCM Code';
      }

      return updated;
    });

    this.gridApi.setGridOption('columnDefs', newDefs);
    this.gridApi.setColumnsVisible(['CostType'], true);
    this.gridApi.stopEditing(true);
    this.gridApi.refreshClientSideRowModel('aggregate');
  }

  private getCapacitySubCategoriesFromIDArray(params) {
    let valueControl = params.value ? params.value : params.data.CapacitySubCategoryIds;
    if (valueControl == null) {
      return '';
    }

    let descriptions: Array<string> = [];

    valueControl.forEach((id) => {
      descriptions.push(this.ICapacitySubCategoryDtoMap[id]);
    });

    return descriptions.join(', ');
  }

  private buildCostTypeList() {
    this.ICostTypeDtos = this.ICostCodeCostTypeDtos
      .filter(d => d.CostTypeDto?.CostTypeCategoryId === 5) // only category 5
      .map(d => ({
        CostTypeId: d.CostTypeId,
        CostTypeDescription: d.CostTypeDto.CostTypeDescription,
        CostTypeCategoryId: d.CostTypeDto.CostTypeCategoryId,
        CostTypeValue: d.CostTypeDto.CostTypeValue
      }))
      .filter((obj, index, self) =>
        index === self.findIndex(t => t.CostTypeId === obj.CostTypeId) // distinct by id
      ).sort((a, b) =>
        a.CostTypeDescription.localeCompare(b.CostTypeDescription)
      );
  }

  private gridApi: GridApi;
  public editMode: boolean = false;
  public loading: boolean = false;
  private readonly agGrid = viewChild(AgGridAngular);
  newItemId = 0;

  getIProjectTypeDtosById = (id) => {
    if (!id) return '';
    let atype = this.IProjectTypeDtos.find(a => a.ProjectTypeId == id);
    if (atype) return atype.Description;
    return '';
  }

  getIFunctionDtosById = (id) => {
    if (!id) return '';
    let atype = this.IFunctionDtos.find(a => a.FunctionId == id);
    if (atype) return atype.Description;
    return '';
  }

  getIUnitOfMeasureDtosById = (id) => {
    if (!id) return '';
    let atype = this.IUnitOfMeasureDtos.find(a => a.UnitOfMeasureId == id);
    if (atype) return atype.Description;
    return '';
  }

  getICostCodeCostTypeDtosById = (id) => {
    if (!id) return '';

    let atype = this.ICostCodeCostTypeDtos.find(a => a.CostCodeCostTypeId == id);
    if (atype)
      return (atype.CostCodeDto.CostCodeValue + ' - ' + atype.CostCodeDto.CostCodeDescription);
    return '';
  }

  getShortICostCodeCostTypeDtosById = (id) => {
    if (!id) return '';

    let atype = this.ICostCodeCostTypeDtos.find(a => a.CostCodeCostTypeId == id);
    if (atype) return (atype.CostCodeDto.CostCodeValue + ' - ' + atype.CostCodeDto.CostCodeDescription);
    return '';
  }

  getICostTypeDtosById = (id) => {
    if (!id) return '';

    let atype = this.ICostTypeDtos.find(a => a.CostTypeValue == id);
    if (atype) return (atype.CostTypeDescription);
    return '';
  }

  getIItemTypeDtosById = (id) => {
    if (!id) return '';
    let atype = this.IItemTypeDtos.find(a => a.ItemCategoryId == id);
    if (atype) return atype.ItemCategoryName;
    return '';
  }

  getIItemSubTypeDtosById = (id) => {
    if (!id) return '';
    let atype = this.IItemSubTypeDtos.find(a => a.ItemSubCategoryId == id);
    if (atype) return atype.ItemSubCategoryName;
    return '';
  }

  public async loadItemScopingCategories() {
    this.ItemScopingCategoryList = (await this.lookupService.findItemScopingCategories()).value;
    this.ItemScopingCategoryMap = new Map();
    this.ItemScopingCategoryList.map(x => this.ItemScopingCategoryMap[x.ItemScopingCategoryId] = x.Description);
  }

  private async loadCapacitySubCategories() {
    this.ICapacitySubCategoryDtos = (await this.lookupService.findCapacitySubCategories()).value;
    this.ICapacitySubCategoryDtoMap = new Map();
    this.ICapacitySubCategoryDtos.map(x => this.ICapacitySubCategoryDtoMap[x.CapacitySubCategoryId] = x.Description);
  }

  private BASE_URL = this.environment.apiUrl;
  private ItemScopingCategoryList: Array<ItemScopingCategory> = new Array<ItemScopingCategory>();
  private ItemScopingCategoryMap: {};

  async ngOnInit() {
    try {
      this.ICostCodeCostTypeDtos = (await this.lookupService.findCostCodeCostTypeDtos()).Results;
      this.buildCostTypeList();
      this.IProjectTypeDtos = (await this.lookupService.findProjectTypeDtos()).Results;
      this.IFunctionDtos = (await this.lookupService.findFunctionDtos()).Results;
      this.IItemTypeDtos = (await this.itemManagementService.findItemTypeDtos()).Results;
      this.IItemSubTypeDtos = (await this.itemManagementService.findItemSubTypeDtos()).Results
      this.IUnitOfMeasureDtos = (await this.lookupService.findUnitOfMeasureDtos()).Results;
      await this.loadCapacitySubCategories();
      await this.loadItemScopingCategories();

      this.ScopingItemGroupMapping = await this.itemManagementService.getScopingItemGroupMappings();

      this.initLoaded.next(true)
    }
    catch (error) {
      this.errorService.error("Error loading data (ngOnInit)", error);
    }
  }

  public isInitLoaded(): Observable<boolean> {
    return this.initLoaded.asObservable();
  }

  async loadGrid() {
    this.gridApi.setGridOption('loading', true);
    this.loading = true;
    this.editMode = false;
    this.editedEntities = new Map<number, IItemDto>();
    this.ValidationErrors = new Map<string, Array<string>>();
    this.newItemId = 0;
    let data = await this.itemManagementService.findItemDtos();
    this.IItemDtos = data.Results;
    this.gridApi.setGridOption('rowData', this.IItemDtos)
    if (this.IItemDtos.length) { //AgGrid will freeze if filter is applied to empty data
      await this.gridApi.setColumnFilterModel('IsActive', { values: ["true"] });
    }
    this.gridApi.setGridOption('loading', false);
    this.gridApi.onFilterChanged();
    this.loading = false;
    this.applyAfeColumnState();
  }

  defaultExcelExportParams = {
    processCellCallback: (params) => {
      if (params.column.userProvidedColDef && params.column.userProvidedColDef.type
        && params.column.userProvidedColDef && params.column.userProvidedColDef.type.length > 0
        && params.column.userProvidedColDef.type.includes('dateColumn')
        && params.value) {
        return params.value ? moment(params.value).format('L') : ''
      }
      return params.value;
    },
  };

  onCellValueChanged(e) {
    this.trackChanges(e);
  }

  async onGridReady(params) {
    this.gridApi = params.api;
    this.isInitLoaded().subscribe(response => {
      this.loadGrid();
    });
  }

  addNewItem() {
    let dta = <IItemDto>{
      ItemId: this.newItemId--
      , ItemNumber: 0
      , Name: ''
      , IsActive: true
    };

    this.IItemDtos.push(dta);
    this.editMode = true;
    this.agGrid().api.applyTransaction({
      add: [dta], addIndex: 0
    });
  }

  trackChanges(e) {
    this.editMode = true;
    let editedItem: IItemDto = {
      ItemId: e.data.ItemId
      , ItemNumber: this.getValueOrZero(e.data.ItemNumber)
      , Name: e.data.Name
      , FunctionId: this.getValueOrZero(e.data.FunctionId)
      , ItemCategoryId: this.getValueOrZero(e.data.ItemCategoryId)
      , ItemSubCategoryId: this.getValueOrZero(e.data.ItemSubCategoryId)
      , PartNumber: e.data.PartNumber
      , OilCapacity: e.data.OilCapacity
      , GasCapacity: e.data.GasCapacity
      , WaterCapacity: e.data.WaterCapacity
      , ElectricityDemand: e.data.ElectricityDemand
      , UsePhaseStepStartDate: e.data.UsePhaseStepStartDate
      , CostCodeCostTypeId: this.getValueOrZero(e.data.CostCodeCostTypeId)
      , PrimaryUOM: this.getValueOrZero(e.data.PrimaryUOM)
      , IsActive: e.data.IsActive
      , ItemScopingCategoryId: e.data.ItemScopingCategoryId
      , CapacitySubCategoryIds: e.data.CapacitySubCategoryIds
    };
    this.editedEntities.set(e.data.ItemId, editedItem);
  }

  getValueOrZero(value?: number) {
    if (value === undefined || value === null) {
      return 0;
    } else {
      return value;
    }
  }

  public async cancelChanges() {
    await this.loadGrid();
  }

  public async saveChanges() {
    this.loading = true;
    if (this.editedEntities.size === 0) {
      this.loading = false;
      return;
    }

    this.ValidationErrors = new Map<string, Array<string>>();
    let array = Array.from(this.editedEntities.values());

    array.forEach(element => {
      let obj = this.itemDtoValidator.validate(element);
      const values = Object.values(obj);
      if (values.length > 0) {
        let messages = new Array<string>();

        values.forEach(message => {
          messages.push(message as string)
        });
        this.ValidationErrors.set('Item with Item Number of ' + Math.abs(element.ItemNumber).toString() + ' has the following error(s):', messages);
      }

      if (element.ItemScopingCategoryId == undefined) {
        let group = this.ScopingItemGroupMapping.find(f => f.ScopingItemId === element.ItemId);

        if (group !== undefined && group.MappedDependentItems.length > 0) {
          let messages = new Array<string>();
          messages.push("Cannot remove Scoping Category from Items that have dependent items assigned");
          this.ValidationErrors.set('Item with Item Number of ' + Math.abs(element.ItemNumber).toString() + ' has the following error(s):', messages);
        }
      }
    });

    if (this.ValidationErrors.size > 0) {
      this.loading = false;
      return;
    }

    const uiDuplicates = this.getDuplicateCombos(array);

    if (uiDuplicates.length > 0) {
      this.ValidationErrors.set('You have entered a duplicate item in your list. Each Name, Cost Type, and GFCM Code combination must be unique.', []);
      this.loading = false;
      return;
    }

    this.itemManagementService.ifItemsExist(array).subscribe(existingItems => {
      if (existingItems.length > 0) {
        let messages = new Array<string>();
        messages.push(existingItems
          .map(x => `Name: ${x.Name}, ProjectCostType: ${x.ProjectCostType}, GFCMCode: ${x.GFCMCode}`)
          .join(''));
        this.ValidationErrors.set('An identical combination of Name, Project Cost Type, and GFCM Code already exist: ', messages);
        this.loading = false;
        return;
      }

      this.itemManagementService.upsertItemDtos(array).subscribe(() => {
        this.loadGrid();
      });
    });
  }

  private getDuplicateCombos(array: IItemDto[]): IItemDto[] {
    const groups = array.reduce((acc, item) => {
      const key = `${item.Name?.trim().toUpperCase()}-${item.CostCodeCostTypeId}`;
      (acc[key] = acc[key] || []).push(item);
      return acc;
    }, {} as Record<string, IItemDto[]>);

    return Object.values(groups)
      .filter(g => g.length > 1)
      .flat();
  }
}